import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { Schedule } from 'src/providers/schedule/schedule';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'dynamic-booking-form',
  templateUrl: './dynamic-booking-form.component.html',
  styleUrls: ['./dynamic-booking-form.component.scss'],
  standalone: false
})
export class DynamicBookingFormComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() forms: any[];
  @Input() submitted = false;
  @Input() hasImageUploadError = false;
  @Input() isLoading = false;
  @Output() formDataChange = new EventEmitter<any>();
  @Output() attachmentsChange = new EventEmitter<any[]>();
  @Output() selectedCheckboxesChange = new EventEmitter<any[]>();

  form: FormGroup;
  attachments: any[] = [];
  selectedCheckboxes: any[] = [];
  errorMessage = '';

  // Subject for cleanup
  private destroy$ = new Subject<void>();

  private readonly fb = inject(FormBuilder);
  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly scheduleService = inject(Schedule);

  // Getter to check if component is ready
  get isReady(): boolean {
    return !!(this.form && this.forms && this.forms[0] && this.forms[0].inputs);
  }

  constructor() {
    this.form = this.fb.group({});
  }

  ngOnInit() {
    try {
      if (!this.forms || this.forms.length === 0) {
        return;
      }
      // Ensure form is initialized before setting up controls
      if (!this.form) {
        this.form = this.fb.group({});
      }
      this.initializeDynamicFormControls();

      // Set up form value change monitoring with debounce
      this.setupFormValueChangeMonitoring();

      // Emit that component is initialized
      this.formDataChange.emit({
        type: 'componentInitialized',
        isReady: this.isReady,
      });
      console.log('forms', this.forms);
    } catch (error) {
      console.error('Error initializing dynamic booking form:', error);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reinitialize form controls when forms input changes
    if (
      changes.forms &&
      changes.forms.currentValue &&
      !changes.forms.firstChange
    ) {
      // Clear existing form and reinitialize
      this.form = this.fb.group({});
      this.initializeDynamicFormControls();

      // Set up form value change monitoring for the new form
      this.setupFormValueChangeMonitoring();

      // Emit that the component is ready for external data
      setTimeout(() => {
        this.formDataChange.emit({
          type: 'formsLoaded',
          forms: this.forms,
          form: this.form,
          isReady: this.isReady,
        });
      }, 100); // Small delay to ensure form is fully initialized
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Set up form value change monitoring with debounce
   * Emits form data changes when user types, with a 500ms debounce
   */
  private setupFormValueChangeMonitoring() {
    if (this.form) {
      this.form.valueChanges
        .pipe(
          debounceTime(500), // Wait 500ms after user stops typing
          distinctUntilChanged(), // Only emit if value actually changed
          takeUntil(this.destroy$) // Clean up when component is destroyed
        )
        .subscribe((formValues) => {
          // Emit form data change event
          this.formDataChange.emit({
            type: 'formDataChanged',
            formValues,
            formData: this.buildBookingFormData(),
          });
        });
    }
  }

  initializeDynamicFormControls() {
    // Ensure form exists before proceeding
    if (!this.form) {
      this.form = this.fb.group({});
    }

    if (this.forms && this.forms[0] && this.forms[0].inputs) {
      this.forms[0].inputs.forEach((field) => {
        // Transform data.options to items if it exists
        if (field.data && field.data.options && !field.items) {
          field.items = field.data.options.map(option => ({
            value: option.key,
            viewValue: option.label,
            responseValue: option.value
          }));
        }

        if (field.id) {
          const controlName = field.id.toString();

          // Create control with validation based on field requirements
          const validators = [];
          if (field.is_required) {
            validators.push(Validators.required);
          }

          this.safeAddControl(controlName, this.fb.control('', validators));

          // Add text area control for select fields that might require additional text
          if (field.type === 'select' && field.requiresTextArea) {
            const textAreaControlName = `${controlName  }textArea`;
            const textAreaValidators = [];
            if (field.is_required) {
              textAreaValidators.push(Validators.required);
            }
            this.safeAddControl(
              textAreaControlName,
              this.fb.control('', textAreaValidators)
            );
          }
        }
      });
    }
  }

  changeSelectValue(value, index) {
    if (!this.form || !this.forms?.[0]?.inputs?.[index]) {return;}

    if (!value.responseValue) {
      const controlName =
        `${this.forms[0].inputs[index].id.toString()  }textArea`;
      this.safeAddControl(controlName, this.fb.control(''));
      const control = this.form.get(controlName);
      if (control) {
        control.setValidators([Validators.required]);
        control.updateValueAndValidity();
      }
      this.forms[0].inputs[index].requiresTextArea = true;
    } else {
      this.forms[0].inputs[index].requiresTextArea = false;
      const controlName =
        `${this.forms[0].inputs[index].id.toString()  }textArea`;
      const control = this.form.get(controlName);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    }
  }

  getFormControl(fieldId: string) {
    if (!fieldId || !this.form || !this.isReady) {return null;}
    const controlName = fieldId.toString();
    return this.form.contains(controlName) ? this.form.get(controlName) : null;
  }

  selectCheckbox(selected, checkbox, field, index) {
    if (selected) {
      let valueAlreadyAdded = false;
      let inputAlreadyAdded = false;
      this.selectedCheckboxes.map((selections) => {
        if (selections.input_id === field.id) {
          inputAlreadyAdded = true;
          selections.value.map((value) => {
            if (Object.keys(value)[0] === checkbox.key) {
              valueAlreadyAdded = true;
            }
          });
          if (inputAlreadyAdded && !valueAlreadyAdded) {
            selections.value.push({
              [checkbox.key]: checkbox.value,
            });
          }
        }
      });
      if (!valueAlreadyAdded && !inputAlreadyAdded) {
        this.selectedCheckboxes.push({
          input_id: field.id,
          value: [
            {
              [checkbox.key]: checkbox.value,
            },
          ],
        });
      }
      if (!checkbox.value) {
        this.changeSelectValue({}, index);
      }
    }
    if (!selected) {
      this.selectedCheckboxes.map((item, i) => {
        if (item.input_id === field.id) {
          const array = [];
          item.value.map((value) => {
            if (Object.keys(value)[0] !== checkbox.key) {
              array.push(value);
            }
          });
          item.value = array;
          if (item.value.length === 0) {
            this.selectedCheckboxes.splice(i, 1);
          }
        }
      });
      if (!checkbox.value) {
        this.changeSelectValue({ responseValue: 'hasValue' }, index);
      }
    }
    this.selectedCheckboxesChange.emit(this.selectedCheckboxes);
  }

  async addAttachment(field) {
    this.errorMessage = '';
    try {
      const attachment = await this.conciergeApi.addAttachment();
      if (attachment.filename !== '') {
        this.attachments.map((existingAttachment, i) => {
          if (existingAttachment.input_id === field.input_id) {
            this.attachments.splice(i, 1);
          }
        });

        let value;
        if (field.type === 'files') {
          const attachmentRef = this.attachments.find(
            (att) => att.input_id === field.id
          );
          if (attachmentRef) {
            return attachmentRef.value.push(attachment.url);
          }
          value = [attachment.url];
        } else {
          value = attachment.url;
        }

        this.attachments.push({
          input_id: field.id,
          value,
          fileName: attachment.filename,
        });
        this.hasImageUploadError = false;
        this.attachmentsChange.emit(this.attachments);
      } else {
        this.errorMessage =
          'Unable to attach photo. Please upload a PNG or JPEG file.';
      }
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  getAttachment(field) {
    return this.attachments.find(
      (attachment) => attachment.input_id === field.id
    );
  }

  removeAttachment(field, imageUrl = null) {
    if (imageUrl) {
      const attachmentRef = this.getAttachment(field);
      if (attachmentRef) {
        attachmentRef.value = attachmentRef.value.filter(
          (url) => url !== imageUrl
        );
        if (attachmentRef.value.length > 0) {
          return;
        }
      }
    }

    const attachmentIndex = this.attachments.findIndex(
      (attachment) => attachment.input_id === field.id
    );
    if (attachmentIndex !== -1) {
      this.attachments.splice(attachmentIndex, 1);
    }
    this.attachmentsChange.emit(this.attachments);
  }

  buildBookingFormData() {
    const bookingFormData = {
      input_answers: [],
    };

    if (!this.canPerformFormOperations()) {
      return bookingFormData;
    }

    this.forms[0].inputs.forEach((field, i) => {
      // Handle image fields - get value from attachments
      if (field.data?.type === 'image' || field.data?.type === 'images') {
        const attachment = this.attachments.find(
          (item) => item.input_id === field.id
        );
        if (attachment) {
          bookingFormData.input_answers.push({
            input_id: field.id,
            value: attachment.value,
          });
        } else if (field.is_required) {
          // Mark as error if required field is missing
          this.forms[0].inputs[i]['imageUploadError'] = true;
          this.hasImageUploadError = true;
        }
      } else {
        // Handle regular form fields
        const controlName = field.id.toString();
        const control = this.form.get(controlName);
        const currentValue = control?.value;

        // Check if field is required and has a value
        const hasValue =
          currentValue !== null &&
          currentValue !== undefined &&
          currentValue !== '';

        if (hasValue) {
          let finalValue = currentValue;

          // Handle select fields with potential text area additions
          if (field.type === 'select') {
            const item = field.items?.find(
              (selectItem) => selectItem.value === currentValue
            );
            const selectObject = {};
            const textAreaValue = this.form.get(
              `${controlName  }textArea`
            )?.value;
            selectObject[currentValue] =
              item?.responseValue || textAreaValue || currentValue;
            finalValue = selectObject;
          }

          bookingFormData.input_answers.push({
            input_id: field.id,
            value: finalValue,
          });
        } else if (field.is_required) {
          // Mark field as invalid if required but no value
          control?.markAsTouched();
          control?.markAsDirty();
        }
      }
    });

    // Add checkbox selections to the input_answers
    this.selectedCheckboxes.forEach((checkbox) => {
      // Handle checkbox "other" fields that may have text areas
      checkbox.value.forEach((value, i) => {
        const isOtherField = Object.values(value).includes(null);
        if (isOtherField) {
          const object = {};
          object[Object.keys(value)[0]] = this.form.get(
            `${checkbox.input_id  }textArea`
          )?.value;
          checkbox.value[i] = object;
        }
      });

      bookingFormData.input_answers.push({
        input_id: checkbox.input_id,
        value: checkbox.value,
      });
    });

    return bookingFormData;
  }

  patchFormWithOriginalData(originalFormAnswers) {
    if (!this.canPerformFormOperations() || !originalFormAnswers) {return;}

    // Convert to the expected array format
    const answersArray = this.convertToAnswersArray(originalFormAnswers);

    if (answersArray.length === 0) {
      console.warn(
        'No valid answers found in originalFormAnswers:',
        originalFormAnswers
      );
      return;
    }

    answersArray.forEach((answer) => {
      try {
        // The answer structure uses template_input.id instead of input_id
        const templateInputId = answer.template_input?.id || answer.input_id;
        const answerValue =
          answer.data?.value !== undefined ? answer.data.value : answer.value;

        if (templateInputId && this.form.contains(templateInputId.toString())) {
          const controlName = templateInputId.toString();

          // Type may be on answer.template_input.type (reschedule format) or
          // absent (buildBookingFormData format) — fall back to the input def.
          const inputDef = this.forms?.[0]?.inputs?.find(
            (i) => i.id?.toString() === templateInputId.toString()
          );
          const type = answer.template_input?.type || inputDef?.type;

          if (type === 'select') {
            // Value can be a plain key string (reschedule) or {key: response}
            // (buildBookingFormData). Patch the form with the key only.
            let valueToPatch = answerValue;
            if (
              answerValue &&
              typeof answerValue === 'object' &&
              !Array.isArray(answerValue)
            ) {
              valueToPatch = Object.keys(answerValue)[0];
            }
            this.form.get(controlName)?.patchValue(valueToPatch);

            if (answer.additional_text) {
              const textAreaControlName = `${controlName  }textArea`;
              if (this.form.contains(textAreaControlName)) {
                this.form
                  .get(textAreaControlName)
                  ?.patchValue(answer.additional_text);
              }
            }
          } else {
            // text, text_area, or unknown — patch the value directly.
            this.form.get(controlName)?.patchValue(answerValue);
          }
        }
      } catch (_error) {
        // Silent error handling - individual input processing should not break the form
      }
    });
  }

  async loadOriginalAttachments(originalFormAnswers) {
    // Clear existing attachments
    this.attachments = [];

    if (
      originalFormAnswers &&
      this.forms &&
      this.forms[0] &&
      this.forms[0].inputs
    ) {
      // Convert to the expected array format
      const answersArray = this.convertToAnswersArray(originalFormAnswers);

      if (answersArray.length === 0) {
        console.warn(
          'No valid answers found for attachments:',
          originalFormAnswers
        );
        return;
      }

      this.forms[0].inputs.forEach((field) => {
        // Check if this is an image field
        if (field.data?.type === 'image' || field.data?.type === 'images') {
          // Find the corresponding answer — support both reschedule format
          // ({template_input, data}) and buildBookingFormData format
          // ({input_id, value}).
          const originalAnswer = answersArray.find(
            (answer) =>
              answer.template_input?.id === field.id ||
              answer.input_id === field.id ||
              answer.input_id?.toString() === field.id?.toString()
          );

          const attachmentValue =
            originalAnswer?.data?.value !== undefined
              ? originalAnswer.data.value
              : originalAnswer?.value;

          if (originalAnswer && attachmentValue) {
            // Create attachment object in the expected format
            const attachment = {
              input_id: field.id,
              value: attachmentValue,
              fileName: this.getFileNameFromUrl(attachmentValue),
            };

            this.attachments.push(attachment);
          }
        }
      });

      this.attachmentsChange.emit(this.attachments);
    }
  }

  // Helper method to extract filename from URL
  getFileNameFromUrl(url) {
    if (typeof url === 'string') {
      const parts = url.split('/');
      return parts[parts.length - 1] || 'image';
    } else if (Array.isArray(url) && url.length > 0) {
      // For multiple images, show count
      return `${url.length} image(s)`;
    }
    return 'image';
  }

  // Method to get the form value for external use
  getFormValue() {
    return this.form.value;
  }

  // Method to get the form instance for external use
  getForm() {
    return this.form;
  }

  // Method to check if form is valid (no side effects — safe to call from templates)
  isFormValid(): boolean {
    if (!this.form || !this.isReady) {
      return false;
    }

    // Check required fields without marking them as touched/dirty
    let requiredFieldsValid = true;
    if (this.forms?.[0]?.inputs) {
      for (const field of this.forms[0].inputs) {
        if (field.is_required) {
          const control = this.form.get(field.id.toString());
          const value = control?.value;
          if (!value || value === '' || value === null || value === undefined) {
            requiredFieldsValid = false;
            break;
          }
        }
      }
    }

    return requiredFieldsValid && this.form.valid && !this.hasImageUploadError;
  }

  // Method to mark all fields as touched for validation display
  markFormAsTouched() {
    if (!this.form) {return;}

    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      control?.markAsTouched();
    });
  }

  // Method to validate all required fields
  validateRequiredFields(): boolean {
    if (!this.form || !this.isReady) {
      return false;
    }

    let isValid = true;

    if (this.forms && this.forms[0] && this.forms[0].inputs) {
      this.forms[0].inputs.forEach((field) => {
        if (field.is_required) {
          const controlName = field.id.toString();
          const control = this.form.get(controlName);

          if (control) {
            const value = control.value;

            // Check if required field is empty
            if (
              !value ||
              value === '' ||
              value === null ||
              value === undefined
            ) {
              control.markAsTouched();
              control.markAsDirty();
              isValid = false;
            }
          }
        }
      });
    }

    return isValid;
  }

  // Public method to manually reinitialize the form
  reinitializeForm() {
    this.form = this.fb.group({});
    this.initializeDynamicFormControls();
    this.setupFormValueChangeMonitoring();
  }

  // Method to check if component can accept external data
  canAcceptExternalData(): boolean {
    return this.isReady && !!this.form;
  }

  // Method to safely load external data
  async safeLoadExternalData(originalFormAnswers: any): Promise<void> {
    if (!this.canAcceptExternalData()) {
      // Wait a bit and try again
      await new Promise((resolve) => setTimeout(resolve, 100));
      if (!this.canAcceptExternalData()) {
        return;
      }
    }

    this.patchFormWithOriginalData(originalFormAnswers);
    await this.loadOriginalAttachments(originalFormAnswers);

    // Emit that data has been loaded
    this.formDataChange.emit({
      type: 'externalDataLoaded',
      originalFormAnswers,
    });
  }

  // Safe method to add form control
  private safeAddControl(controlName: string, control: any) {
    if (!this.form) {
      console.error('Form is not initialized when trying to add control:', controlName);
      this.form = this.fb.group({});
    }
    if (!this.form.contains(controlName)) {
      try {
        this.form.addControl(controlName, control);
      } catch (_error) {
        console.error('Error adding control:', controlName, _error);
        // Silent error - control add failure is not critical
      }
    }
  }

  // Method to safely check if we can perform form operations
  private canPerformFormOperations(): boolean {
    return !!(this.form && this.isReady);
  }

  // Helper method to convert different data structures to the expected format
  private convertToAnswersArray(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }

    if (typeof data === 'object' && data !== null) {
      // If it's an object with input_id keys, convert to array format
      return Object.keys(data).map((inputId) => ({
        template_input: { id: parseInt(inputId) },
        data: { value: data[inputId] },
      }));
    }

    return [];
  }

  // Method to get validation status for a specific field
  getFieldValidationStatus(fieldId: string): {
    isValid: boolean;
    isRequired: boolean;
    hasValue: boolean;
  } {
    if (!this.canPerformFormOperations()) {
      return { isValid: false, isRequired: false, hasValue: false };
    }

    const controlName = fieldId.toString();
    const control = this.form.get(controlName);

    if (!control) {
      return { isValid: false, isRequired: false, hasValue: false };
    }

    const field = this.forms[0].inputs.find(
      (input) => input.id.toString() === fieldId
    );
    const isRequired = field?.is_required || false;
    const hasValue =
      control.value !== null &&
      control.value !== undefined &&
      control.value !== '';
    const isValid = !isRequired || hasValue;

    return { isValid, isRequired, hasValue };
  }

  // Method to check if a field should show validation error
  shouldShowFieldError(fieldId: string): boolean {
    if (!this.canPerformFormOperations()) {
      return false;
    }

    const controlName = fieldId.toString();
    const control = this.form.get(controlName);

    if (!control) {
      return false;
    }

    const field = this.forms[0].inputs.find(
      (input) => input.id.toString() === fieldId
    );
    const isRequired = field?.is_required || false;

    if (!isRequired) {
      return false;
    }

    const hasValue =
      control.value !== null &&
      control.value !== undefined &&
      control.value !== '';

    // Show error if field is required, has no value, and either:
    // 1. Form has been submitted, OR
    // 2. Field has been touched/dirty (user has interacted with it)
    return !hasValue && (this.submitted || control.touched || control.dirty);
  }

  // Method to handle field blur events
  onFieldBlur(fieldId: string) {
    if (!this.canPerformFormOperations()) {
      return;
    }

    const controlName = fieldId.toString();
    const control = this.form.get(controlName);

    if (control) {
      control.markAsTouched();
    }
  }

  // Method to get detailed validation info for debugging
  getValidationDebugInfo(fieldId: string): any {
    if (!this.canPerformFormOperations()) {
      return { error: 'Component not ready' };
    }

    const controlName = fieldId.toString();
    const control = this.form.get(controlName);

    if (!control) {
      return { error: 'Control not found' };
    }

    const field = this.forms[0].inputs.find(
      (input) => input.id.toString() === fieldId
    );

    return {
      fieldId,
      controlName,
      value: control.value,
      touched: control.touched,
      dirty: control.dirty,
      valid: control.valid,
      errors: control.errors,
      isRequired: field?.is_required || false,
      shouldShowError: this.shouldShowFieldError(fieldId),
      submitted: this.submitted,
    };
  }

  // Method to wait for component to be ready
  async waitForReady(): Promise<boolean> {
    let attempts = 0;
    const maxAttempts = 50; // Wait up to 5 seconds

    while (attempts < maxAttempts) {
      if (this.isReady) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
      attempts++;
    }

    return false;
  }
}
